import React from "react";
import {
  PhoneCall,
  FileText,
  Banknote,
  Activity,
  Clock,
  Target,
  TrendingUp,
} from "lucide-react";

/**
 * Bandeau de KPIs (4 à 6 cartes), inspiré d'Axonaut.
 * Affiche : appels, durée totale, devis, CA payé, dernier contact, montant pondéré (opp).
 *
 * Props :
 *   kpis : objet renvoyé par l'endpoint /overview
 *   kind : "prospect" | "opportunity"
 */
const minsLabel = (mins) => {
  if (!mins) return "0 min";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
};

const hoursAgo = (h) => {
  if (h === null || h === undefined) return "—";
  if (h < 1) return "à l'instant";
  if (h < 24) return `il y a ${Math.round(h)} h`;
  if (h < 24 * 7) return `il y a ${Math.round(h / 24)} j`;
  if (h < 24 * 30) return `il y a ${Math.round(h / (24 * 7))} sem.`;
  return `il y a ${Math.round(h / (24 * 30))} mois`;
};

const eur = (n) =>
  Number(n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const Kpi = ({ icon: Icon, label, value, sub, accent = "#f7c935", testId }) => (
  <div
    className="bg-[#0b0811] dark:bg-[#0b0811] rounded-xl border border-white/5 p-3.5 hover:border-white/10 transition-colors"
    data-testid={testId}
  >
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
      <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
      {label}
    </div>
    <div className="text-lg md:text-xl font-bold text-white mt-1.5 truncate">{value}</div>
    {sub && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>}
  </div>
);

export const EntityKpiBar = ({ kpis, kind, testId = "entity-kpi-bar" }) => {
  if (!kpis) return null;
  const items = [];

  items.push({
    icon: PhoneCall,
    label: "Appels",
    value: kpis.calls_total || 0,
    sub: `${kpis.calls_inbound || 0} entrants · ${kpis.calls_outbound || 0} sortants`,
    accent: "#22c55e",
    testId: "kpi-calls",
  });
  items.push({
    icon: Clock,
    label: "Durée totale",
    value: minsLabel(kpis.calls_duration_minutes || 0),
    sub: hoursAgo(kpis.hours_since_last_call) + " (dernier)",
    accent: "#3b82f6",
    testId: "kpi-call-duration",
  });
  items.push({
    icon: FileText,
    label: "Devis",
    value: `${kpis.devis_signed || 0}/${kpis.devis_count || 0}`,
    sub: `${kpis.devis_signed || 0} signés`,
    accent: "#f97316",
    testId: "kpi-devis",
  });
  items.push({
    icon: Banknote,
    label: "CA encaissé",
    value: eur(kpis.ca_paye_ttc || 0),
    sub: kpis.ca_pending_ttc ? `${eur(kpis.ca_pending_ttc)} en attente` : "à jour",
    accent: "#a855f7",
    testId: "kpi-revenue",
  });

  if (kind === "opportunity") {
    items.push({
      icon: Target,
      label: "Montant",
      value: eur(kpis.amount || 0),
      sub: `Probabilité ${kpis.probability || 0}%`,
      accent: "#f7c935",
      testId: "kpi-amount",
    });
    items.push({
      icon: TrendingUp,
      label: "Pondéré",
      value: eur(kpis.weighted_amount || 0),
      sub: `Stage ${kpis.stage || "—"}`,
      accent: "#06b6d4",
      testId: "kpi-weighted",
    });
  } else {
    items.push({
      icon: Activity,
      label: "Activités",
      value: kpis.activities_count || 0,
      sub: hoursAgo(kpis.hours_since_creation) + " (créé)",
      accent: "#06b6d4",
      testId: "kpi-activities",
    });
  }

  return (
    <div
      className={`grid grid-cols-2 md:grid-cols-3 ${items.length >= 6 ? "lg:grid-cols-6" : "lg:grid-cols-5"} gap-2.5`}
      data-testid={testId}
    >
      {items.map((it, i) => (
        <Kpi key={i} {...it} />
      ))}
    </div>
  );
};

export default EntityKpiBar;
